/**
 * Badge - the pieces the editor and the saved markup have to agree on.
 *
 * Kept out of both components because a badge that renders one way on the
 * canvas and another in the post is a block validation error, not a styling
 * bug.
 *
 * @package BrandyBlocks
 */

import { __ } from '@wordpress/i18n';

import { getBlockGapValue } from '../../utils/helpers';

/**
 * Which side of the label the icon sits on.
 *
 * Position is DOM order rather than a reversing class: the glyph is decorative
 * and hidden from assistive tech either way, and ordering it in the markup
 * keeps the stylesheet free of a rule that exists only to undo itself.
 */
export const ICON_POSITIONS = [
	{ label: __( 'Left', 'brandy-blocks' ), value: 'left' },
	{ label: __( 'Right', 'brandy-blocks' ), value: 'right' },
];

/**
 * Inline style for the badge wrapper.
 *
 * Only the icon-to-text gap: everything else the badge paints — colour,
 * padding, border, type — is core support, which serializes itself. Core writes
 * `blockGap` through the layout support alone, and this block has no layout, so
 * the attribute would render a working control behind a dead value if it were
 * not read out here.
 *
 * @param {Object} style Stored `style` attribute.
 * @return {Object} React style object; empty when no gap is set.
 */
export const badgeStyle = ( style ) => {
	const gap = getBlockGapValue( style?.spacing?.blockGap );

	return gap ? { '--brandy-badge-gap': gap } : {};
};

/**
 * Inline style for the glyph.
 *
 * Left alone the glyph is `1em`, so it tracks the label's font size. A number
 * here breaks that link deliberately — the dot in a pill badge usually wants to
 * be smaller than the text it sits beside.
 *
 * @param {number} iconSize Stored icon size in pixels; undefined to follow the text.
 * @return {Object|undefined} React style object, or undefined when unset.
 */
export const iconStyle = ( iconSize ) =>
	iconSize ? { fontSize: `${ iconSize }px` } : undefined;
